// Timezone-safe handling of calendar dates (e.g. date of birth).
//
// A date of birth has no time and no timezone: Nov 2, 2011 is Nov 2 everywhere.
// The bug this fixes: constructing `new Date("2011-11-02")` (or an ISO instant like
// "2011-11-02T00:00:00.000Z") produces a UTC instant, and formatting it with the
// viewer's local timezone can roll it back to the previous calendar day. We must never
// route a calendar date through a timezone-aware Date for display or day math.
//
// Inputs accepted (whatever the API returns): "2011-11-02" or "2011-11-02T00:00:00.000Z"
// or a Date object. We always reduce to the Y/M/D the value names, no timezone applied.

// Extract {year, month, day} from a date-only value without any timezone conversion.
// Returns null if the value can't be parsed as a calendar date.
export function extractYMD(value) {
  if (value == null || value === '') return null;

  // Plain string like "2011-11-02" or "2011-11-02T00:00:00.000Z" -> read the date part
  // directly off the string so no timezone is ever applied.
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
    if (!m) return null;
    return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
  }

  // Date object (defensive — shouldn't happen once the backend returns strings).
  // Use the UTC parts, because a DATE serialized by pg is midnight UTC of that calendar day.
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return {
      year: value.getUTCFullYear(),
      month: value.getUTCMonth() + 1,
      day: value.getUTCDate(),
    };
  }

  return null;
}

// Format a calendar date for display, e.g. "11/2/2011". Falls back to em dash when empty
// and to the raw string when it isn't a recognizable date (so nothing silently disappears).
export function formatDateOnly(value, { placeholder = '—' } = {}) {
  if (value == null || value === '') return placeholder;
  const ymd = extractYMD(value);
  if (!ymd) return String(value);
  return `${ymd.month}/${ymd.day}/${ymd.year}`;
}

// Whole-years age from a calendar DOB, computed with no timezone involved.
// Returns null for missing/invalid input; callers decide how to render that.
export function calculateAgeFromDateOnly(value, now = new Date()) {
  const ymd = extractYMD(value);
  if (!ymd) return null;
  let age = now.getFullYear() - ymd.year;
  const monthDiff = now.getMonth() + 1 - ymd.month;
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < ymd.day)) {
    age--;
  }
  return age >= 0 ? age : null;
}
